'use client';

import type { ReactNode } from 'react';
import { Check, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { cn } from '@/lib/utils';

interface SetupChoiceOptionRowProps {
  /** Rendered inside the 44×44 muted square — a lucide icon or <img>. */
  icon: ReactNode;
  name: string;
  hint: string;
  selected: boolean;
  onSelect: () => void;
  /** Optional uppercase pill shown next to the name (e.g. "Recommended"). */
  badge?: string;
  /** Optional social-proof stat shown in the primary accent below the hint. */
  proof?: string;
}

/**
 * Selectable choice-card row shared by the guided checkout questions (vertical,
 * upsell). Mirrors `SetupPathStep`'s `PathOptionRow` one-for-one so the guided
 * flow matches the rest of the onboarding exactly.
 */
export function SetupChoiceOptionRow({
  icon,
  name,
  hint,
  selected,
  onSelect,
  badge,
  proof,
}: SetupChoiceOptionRowProps) {
  return (
    <motion.li variants={SETUP_PATH_MOTION.option}>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        className={cn(
          'border-border bg-card flex min-h-[4.75rem] w-full cursor-pointer items-center gap-3.5 rounded-[0.625rem] border px-[1.125rem] py-4 text-left transition-[border-color,background,box-shadow] duration-150',
          !selected &&
            'hover:border-[color-mix(in_srgb,var(--primary)_25%,var(--border))] hover:bg-[color-mix(in_srgb,var(--muted)_35%,var(--card))]',
          selected &&
            'border-[var(--crm-selection-border-strong)] bg-[color-mix(in_srgb,var(--crm-primary-surface)_55%,var(--card))] shadow-[0_0_0_1px_var(--crm-selection-ring)]',
        )}
        onClick={onSelect}
      >
        <span
          className="bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.625rem]"
          aria-hidden
        >
          {icon}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground text-sm font-semibold">{name}</span>
            {badge ? (
              <span className="text-primary rounded-full border border-[var(--crm-primary-surface-border)] bg-[var(--crm-primary-surface)] px-[0.4375rem] py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.06em]">
                {badge}
              </span>
            ) : null}
          </span>
          <span className="text-[0.8125rem] leading-[1.45] text-[color-mix(in_oklch,var(--foreground)_72%,var(--muted-foreground))]">
            {hint}
          </span>
          {proof ? (
            <span className="text-primary mt-0.5 flex items-start gap-1.5 text-[0.75rem] font-medium leading-[1.4]">
              <TrendingUp className="mt-px size-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
              <span>{proof}</span>
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-center justify-center" aria-hidden>
          {selected ? (
            <span className="text-primary-foreground bg-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
              <Check className="size-3" strokeWidth={3} />
            </span>
          ) : (
            <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-[color-mix(in_srgb,var(--muted-foreground)_45%,transparent)]" />
          )}
        </span>
      </button>
    </motion.li>
  );
}
